import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
  useLocation,
  ScrollRestoration,
  Outlet,
} from "react-router";
import "@fontsource/inter/300.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import { ThemeProvider } from "@accredo/fe-shared/theme/index.js";
import { PORTAL } from "@accredo/fe-shared/theme/portals.js";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/de";
import { SnackbarProvider } from "notistack";
import { getEnv } from "@accredo/fe-shared/env.js";
import LoginPage from "@accredo/fe-shared/pages/LoginPage.jsx";
import { HomePage } from "./pages/HomePage.jsx";
import ClaimIndexPage from "./pages/claims/ClaimIndexPage.jsx";
import ClaimShowPage from "./pages/claims/ClaimShowPage.jsx";
import { UserProvider } from "@accredo/fe-shared/contexts/UserContextProvider.jsx";
import { BreadcrumbProvider } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import { ConfirmProvider } from "@accredo/fe-shared/contexts/ConfirmContext.jsx";
import ProtectedRoute from "@accredo/fe-shared/routes/ProtectedRoute.jsx";
import RouteErrorBoundary from "@accredo/fe-shared/routes/RouteErrorBoundary.jsx";
import BackendLayout from "./layouts/BackendLayout.jsx";
import ClaimCreatePage from "./pages/claims/ClaimCreatePage.jsx";
import ContractIndexPage from "./pages/contracts/ContractIndexPage.jsx";
import ObjectIndexPage from "./pages/objects/ObjectIndexPage.jsx";
import UserIndexPage from "./pages/users/UserIndexPage.jsx";
import UserDetailPage from "./pages/users/UserDetailPage.jsx";
import MailMatchingPage from "./pages/mail/MailMatchingPage.jsx";
import PaymentMatchingPage from "./pages/bookings/PaymentMatchingPage.jsx";
import { SubstitutionsPage } from "./pages/SubstitutionsPage.jsx";
import { NotificationsPage } from "./pages/NotificationsPage.jsx";
import { RemindersPage } from "./pages/RemindersPage.jsx";
import BookingIndexPage from "./pages/admin/bookings/BookingIndexPage.jsx";
import PaymentImportShowPage from "./pages/admin/bookings/PaymentImportShowPage.jsx";
import BookingExportShowPage from "./pages/admin/bookings/BookingExportShowPage.jsx";
import CustomerIndexPage from "./pages/admin/customers/CustomerIndexPage.jsx";
import CustomerShowPage from "./pages/admin/customers/CustomerShowPage.jsx";
import UserAccountSettingsPage from "./pages/account/UserAccountSettingsPage.jsx";
import KitchenSinkFormPage from "./pages/dev/KitchenSinkFormPage.jsx";
import { useEffect, useMemo } from "react";
import BankAccountsAdministrationPage from "./pages/admin/BankAccountsAdministrationPage.jsx";
import BankAccountsPage from "./pages/bankaccounts/BankAccountsPage.jsx";
import ConsentDocumentAdministrationPage from "./pages/admin/ConsentDocumentAdministrationPage.jsx";
import ControllingIndexPage from "./pages/controlling/ControllingIndexPage.jsx";
import InsurerIndexPage from "./pages/admin/insurers/InsurerIndexPage.jsx";
import InsurerShowPage from "./pages/admin/insurers/InsurerShowPage.jsx";
import SparteIndexPage from "./pages/admin/sparten/SparteIndexPage.jsx";
import SchadenUrsachenIndexPage from "./pages/admin/schaden-ursachen/SchadenUrsachenIndexPage.jsx";
import ClaimFormIndexPage from "./pages/admin/claim-forms/ClaimFormIndexPage.jsx";
import ClaimFormShowPage from "./pages/admin/claim-forms/ClaimFormShowPage.jsx";

import BlogIndexPage from "./pages/blog/BlogIndexPage.jsx";
import BlogPostShowPage from "./pages/blog/BlogPostShowPage.jsx";

import AdminBlogIndexPage from "./pages/admin/blog/BlogIndexPage.jsx";
import AdminBlogPostCreatePage from "./pages/admin/blog/BlogPostCreatePage.jsx";
import AdminBlogPostShowPage from "./pages/admin/blog/BlogPostShowPage.jsx";
import AdminBlogPostEditPage from "./pages/admin/blog/BlogPostEditPage.jsx";
import ImportSettingsPage from "./pages/admin/imports/ImportSettingsPage.jsx";
import DocumentTypeSettingsPage from "./pages/admin/document-types/DocumentTypeSettingsPage.jsx";
import AdminSettingsPage from "./pages/admin/settings/AdminSettingsPage.jsx";
import ClaimWorkflowPage from "./pages/admin/workflow/ClaimWorkflowPage.jsx";

function NotFoundRedirect() {
  console.error("Route nicht gefunden:", window.location.pathname);
  return <Navigate to="/login" replace />;
}

function SchadenRedirect() {
  const location = useLocation();

  const newPath = location.pathname.replace(/^\/schaden/, "/schaeden");

  return (
    <Navigate
      to={`${newPath}${location.search ?? ""}${location.hash ?? ""}`}
      replace
    />
  );
}

function RootRouteElement() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

/**
 * Main App Component for v2 Design
 * This is the redesigned version of the application
 */
export default function App() {
  useEffect(() => {
    document.title = getEnv("VITE_APP_TITLE") || "Schadenportal";
    const favicon = getEnv("VITE_TENANT_FAVICON") || "disphere_favicon.png";
    const link = document.createElement("link");
    link.rel = "icon";
    link.href = "/" + favicon;
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const router = useMemo(() => {
    return createBrowserRouter(
      createRoutesFromElements(
        <Route
          element={<RootRouteElement />}
          errorElement={<RouteErrorBoundary />}
        >
          <Route path="/login" element={<LoginPage />} />
          <Route element={<ProtectedRoute portal={PORTAL.SCHADENPORTAL} />}>
            {/* Default backend layout with standard max width */}
            <Route
              element={<BackendLayout />}
              errorElement={<RouteErrorBoundary />}
            >
              <Route index element={<Navigate to="/home" replace />} />
              <Route path="/home" element={<HomePage />} />
              <Route path="/schaeden" element={<ClaimIndexPage />} />
              <Route path="/schaeden/:schaden_id" element={<ClaimShowPage />} />
              <Route path="/schaden/*" element={<SchadenRedirect />} />
              <Route
                path="/schaeden/neu/:vertrag_id/:objekt_id"
                element={<ClaimCreatePage />}
              />
              <Route
                path="/schaeden/:schaden_id/kommunikation/:kommunikation_id"
                element={<ClaimShowPage />}
              />
              <Route
                path="/schaden/:schaden_id/kommunikation/:kommunikation_id"
                element={
                  <Navigate
                    replace
                    to={({ params }) =>
                      `/schaeden/${params.schaden_id}/kommunikation/${params.kommunikation_id}`
                    }
                  />
                }
              />

              <Route
                path="/admin/bankaccounts"
                element={<BankAccountsAdministrationPage />}
              />

              <Route path="/bankkonten" element={<BankAccountsPage />} />

              <Route
                path="/admin/consent-documents"
                element={<ConsentDocumentAdministrationPage />}
              />

              <Route path="/admin/vertraege" element={<ContractIndexPage />} />
              <Route path="/admin/objekte" element={<ObjectIndexPage />} />
              <Route path="/admin/users" element={<UserIndexPage />} />
              <Route
                path="/admin/users/assignments"
                element={<UserIndexPage />}
              />
              <Route path="/admin/users/:userId" element={<UserDetailPage />} />
              <Route
                path="/admin/emails/:mainFolder?/:subFolder?/:mailId?"
                element={<MailMatchingPage />}
              />
              <Route
                path="/admin/reminders"
                element={<Navigate to="/admin/workflow" replace />}
              />

              <Route
                path="/admin/buchungen/:activeTab?"
                element={<BookingIndexPage />}
              />

              <Route
                path="/admin/buchungen/imports/:id"
                element={<PaymentImportShowPage />}
              />

              <Route
                path="/admin/buchungen/exports/:id"
                element={<BookingExportShowPage />}
              />

              <Route path="/substitutions" element={<SubstitutionsPage />} />

              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/reminders" element={<RemindersPage />} />

              <Route path="/admin/kunden" element={<CustomerIndexPage />} />
              <Route
                path="/admin/kunden/:customerId"
                element={<CustomerShowPage />}
              />
              <Route
                path="/admin/kunden/:customerId/vertraege"
                element={<CustomerShowPage />}
              />
              <Route
                path="/admin/kunden/:customerId/objekte"
                element={<CustomerShowPage />}
              />
              <Route
                path="/admin/kunden/:customerId/schaeden"
                element={<CustomerShowPage />}
              />
              <Route
                path="/admin/kunden/:customerId/bankaccounts"
                element={<CustomerShowPage />}
              />
              <Route
                path="/admin/kunden/:customerId/benutzer"
                element={<CustomerShowPage />}
              />
              <Route
                path="/admin/kunden/:customerId/einstellungen"
                element={<CustomerShowPage />}
              />

              <Route path="/admin/insurers" element={<InsurerIndexPage />} />
              <Route path="/admin/insurers/:id" element={<InsurerShowPage />} />
              <Route path="/admin/sparten" element={<SparteIndexPage />} />
              <Route
                path="/admin/schaden-ursachen"
                element={<SchadenUrsachenIndexPage />}
              />
              <Route
                path="/admin/claim-forms"
                element={<ClaimFormIndexPage />}
              />
              <Route
                path="/admin/claim-forms/:id"
                element={<ClaimFormShowPage />}
              />
              <Route
                path="/admin/claim-forms/:id/:tab"
                element={<ClaimFormShowPage />}
              />

              <Route path="/blog" element={<BlogIndexPage />} />
              <Route
                path="/blog/:blog_post_id"
                element={<BlogPostShowPage />}
              />

              <Route path="/admin/blog" element={<AdminBlogIndexPage />} />
              <Route
                path="/admin/blog/:blog_post_id"
                element={<AdminBlogPostShowPage />}
              />
              <Route
                path="/admin/blog/:blog_post_id/edit"
                element={<AdminBlogPostEditPage />}
              />
              <Route
                path="/admin/blog/neu"
                element={<AdminBlogPostCreatePage />}
              />

              <Route path="/admin/settings" element={<AdminSettingsPage />} />
              <Route path="/admin/workflow" element={<ClaimWorkflowPage />} />

              <Route path="/admin/imports" element={<ImportSettingsPage />} />
              <Route
                path="/admin/imports/:tab"
                element={<ImportSettingsPage />}
              />

              <Route
                path="/admin/document-types"
                element={<DocumentTypeSettingsPage />}
              />

              <Route
                path="/account/:section?"
                element={<UserAccountSettingsPage />}
              />

              {process.env.NODE_ENV !== "production" && (
                <>
                  <Route
                    path="/dev/forms-kitchen-sink"
                    element={<KitchenSinkFormPage />}
                  />
                </>
              )}
            </Route>

            {/* wide-layout backend for pages that need more space */}
            <Route
              element={<BackendLayout maxContentWidth="120rem" />}
              errorElement={<RouteErrorBoundary />}
            >
              <Route
                path="/admin/buchungen/imports/:id/payment-matching"
                element={<PaymentMatchingPage />}
              />
              <Route path="/controlling" element={<ControllingIndexPage />} />
            </Route>
          </Route>
          <Route path="*" element={<NotFoundRedirect />} />
        </Route>
      )
    );
  }, []);

  return (
    <ThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
        >
          <UserProvider>
            <BreadcrumbProvider>
              <ConfirmProvider>
                <RouterProvider router={router} />
              </ConfirmProvider>
            </BreadcrumbProvider>
          </UserProvider>
        </SnackbarProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
